/**
 * The last thing that happens to the frame.
 *
 * One effect, and it is bloom, and it is aimed at almost nothing.
 *
 * In a world of pale architecture, bloom is dangerous in a way it is not in a
 * dark one: everything is already near the top of the range, so an effect that
 * blooms "the bright parts" blooms the walls, the floor and the ceiling, and
 * the result is a white fog with a game somewhere inside it. What has to glow
 * is a very short list — an enemy's core, a muzzle flash, a round in flight,
 * the way out — and every one of those is drawn with `toneMapped: false`.
 *
 * That is what makes a plain threshold enough here, with no second render and
 * no layer masking: tone mapping rolls the architecture off below the
 * threshold no matter how much light falls on it, and the unlit emissives sail
 * straight past it. The shell physically cannot reach the threshold, so it
 * physically cannot bloom.
 */

import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import type { Tier } from './quality.ts';

export interface Post {
  render(): void;
  resize(width: number, height: number): void;
  /** Follow the governor: its whole job is deciding how many pixels to draw. */
  setPixelRatio(ratio: number): void;
  dispose(): void;
}

/**
 * How much of the frame the blur chain runs at.
 *
 * Bloom is a multi-resolution blur and its cost is entirely pixels, so this is
 * the one dial that matters. A quarter-resolution chain on a phone is a wider,
 * softer glow than a half-resolution one — which is not a problem, because
 * what it is glowing is a handful of small bright objects either way.
 */
const SCALE: Record<Tier, number> = { mobile: 0.25, balanced: 0.5, high: 0.65 };

export function makePost(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  tier: Tier,
): Post {
  const size = renderer.getSize(new THREE.Vector2());
  const scale = SCALE[tier];
  const composer = new EffectComposer(renderer);
  composer.setPixelRatio(renderer.getPixelRatio());
  composer.addPass(new RenderPass(scene, camera));

  const bloom = new UnrealBloomPass(
    new THREE.Vector2(size.x * scale, size.y * scale),
    // Restrained. Bloom is here to say "this is a light", not to soften the
    // picture; anything strong enough to be noticed as an effect is too much.
    0.34,
    0.3,
    // Above what the tone map can produce, so only the unlit emissives qualify.
    1.0,
  );
  composer.addPass(bloom);

  return {
    render(): void {
      composer.render();
    },
    setPixelRatio(ratio: number): void {
      composer.setPixelRatio(ratio);
    },
    resize(width: number, height: number): void {
      composer.setSize(width, height);
      bloom.setSize(width * scale, height * scale);
    },
    dispose(): void {
      composer.dispose();
      bloom.dispose();
    },
  };
}
